import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { security } from '../config/security'
import './ValorKitManager.css'

const ValorKitManager = ({ userRole, userEmpresa, permissoesMenusCache = {}, onBack }) => {
  const podeEditar = security.podeEditarValorKit(userRole, permissoesMenusCache)
  const [valorKitData, setValorKitData] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('kwh')
  const [sortDirection, setSortDirection] = useState('asc')
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    kwh: '',
    valor_kit: '',
    porcentagem: ''
  })
  
  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  // Carregar dados da tabela valor_kit
  const loadValorKitData = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('valor_kit')
        .select('*')
        .order('kwh', { ascending: true })

      // Se usuário tem empresa, filtrar apenas os registros da empresa dele
      if (userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }

      const { data, error } = await query

      if (error) throw error
      
      setValorKitData(data || [])
    } catch (error) {
      console.error('Erro ao carregar dados do valor_kit:', error)
      showNotification('Erro ao carregar dados: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }, [userEmpresa])

  // Carregar dados iniciais
  useEffect(() => {
    loadValorKitData()
  }, [loadValorKitData])

  // Função para mostrar notificações
  const showNotification = (message, type = 'info') => {
    // Implementação simples de notificação
    alert(`${type.toUpperCase()}: ${message}`)
  }

  // Função para validar formulário
  const validateForm = () => {
    const errors = {}
    
    if (!formData.kwh || formData.kwh <= 0) {
      errors.kwh = 'Kwh deve ser um número maior que zero'
    }
    
    if (!formData.valor_kit || formData.valor_kit <= 0) {
      errors.valor_kit = 'Valor do kit deve ser maior que zero'
    }
    
    if (!formData.porcentagem || formData.porcentagem < 0) {
      errors.porcentagem = 'Porcentagem deve ser um número positivo'
    }
    
    // Verificar se já existe um item com o mesmo Kwh (exceto quando editando)
    const existingItem = valorKitData.find(item => 
      item.kwh === parseInt(formData.kwh) && 
      (!editingItem || item.id !== editingItem.id)
    )
    
    if (existingItem) {
      errors.kwh = 'Já existe um item com este valor de Kwh'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Função para salvar item
  const handleSave = async () => {
    if (!podeEditar) {
      showNotification('Seu acesso a Valores de Kit é somente consulta.', 'error')
      return
    }
    if (!validateForm()) {
      return
    }

    setSubmitting(true)
    try {
      const itemData = {
        kwh: parseInt(formData.kwh),
        valor_kit: parseFloat(formData.valor_kit),
        porcentagem: parseFloat(formData.porcentagem),
        empresa_id: userEmpresa?.id // Adicionar empresa_id
      }

      if (editingItem) {
        // Atualizar item existente
        const { error } = await supabase
          .from('valor_kit')
          .update(itemData)
          .eq('id', editingItem.id)

        if (error) throw error
        
        showNotification('Item atualizado com sucesso!', 'success')
      } else {
        // Criar novo item
        const { error } = await supabase
          .from('valor_kit')
          .insert(itemData)

        if (error) throw error
        
        showNotification('Item criado com sucesso!', 'success')
      }

      // Recarregar dados e fechar formulário
      await loadValorKitData()
      handleCloseForm()
      
    } catch (error) {
      console.error('Erro ao salvar item:', error)
      showNotification('Erro ao salvar: ' + error.message, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // Função para excluir item
  const handleDelete = async (item) => {
    if (!podeEditar) {
      showNotification('Seu acesso a Valores de Kit é somente consulta.', 'error')
      return
    }
    if (!window.confirm(`Tem certeza que deseja excluir o item com ${item.kwh} Kwh?`)) {
      return
    }

    try {
      const { error } = await supabase
        .from('valor_kit')
        .delete()
        .eq('id', item.id)

      if (error) throw error
      
      showNotification('Item excluído com sucesso!', 'success')
      await loadValorKitData()
      
    } catch (error) {
      console.error('Erro ao excluir item:', error)
      showNotification('Erro ao excluir: ' + error.message, 'error')
    }
  }

  // Função para abrir formulário de edição
  const handleEdit = (item) => {
    if (!podeEditar) return
    setEditingItem(item)
    setFormData({
      kwh: item.kwh.toString(),
      valor_kit: item.valor_kit.toString(),
      porcentagem: item.porcentagem.toString()
    })
    setShowForm(true)
  }

  // Função para abrir formulário de criação
  const handleCreate = () => {
    if (!podeEditar) return
    setEditingItem(null)
    setFormData({
      kwh: '',
      valor_kit: '',
      porcentagem: '15.00' // Valor padrão
    })
    setFormErrors({})
    setShowForm(true)
  }

  // Função para fechar formulário
  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
    setFormData({
      kwh: '',
      valor_kit: '',
      porcentagem: ''
    })
    setFormErrors({})
  }

  // Função para formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Função para filtrar dados
  const filteredData = valorKitData.filter(item =>
    item.kwh.toString().includes(searchTerm) ||
    formatCurrency(item.valor_kit).toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Função para ordenar dados
  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[sortField]
    const bValue = b[sortField]
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  // Função para alterar ordenação
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  if (loading) {
    return (
      <div className="valor-kit-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="valor-kit-container">
      {/* Header */}
      <div className="valor-kit-header">
        <div className="header-content">
          <h1>💰 {podeEditar ? 'Gerenciar Valores de Kit' : 'Valores de Kit'}</h1>
          <p>
            {podeEditar
              ? 'Configure os valores e porcentagens para diferentes consumos de energia'
              : 'Consulta dos valores e porcentagens para diferentes consumos de energia'}
          </p>
          {onBack && (
            <button onClick={onBack} className="back-button">
              ← Voltar
            </button>
          )}
        </div>
      </div>

      {!podeEditar && (
        <div className="valor-kit-consulta-aviso">
          Seu acesso é somente consulta. Não é possível incluir, editar ou excluir valores de kit.
        </div>
      )}

      {/* Controles */}
      <div className="valor-kit-controls">
        <div className="controls-row">
          <div className="search-container">
            <input
              type="text"
              placeholder="🔍 Buscar por Kwh ou valor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          {podeEditar && (
            <button onClick={handleCreate} className="btn btn-primary">
              ➕ Novo Item
            </button>
          )}
        </div>
      </div>

      {/* Tabela de Dados */}
      <div className="valor-kit-table-container">
        {sortedData.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon">📊</div>
            <h3>Nenhum item encontrado</h3>
            <p>
              {searchTerm 
                ? 'Nenhum item corresponde aos critérios de busca.'
                : (podeEditar
                  ? 'Não há itens cadastrados. Clique em "Novo Item" para começar.'
                  : 'Não há itens cadastrados.')
              }
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="valor-kit-table">
              <thead>
                <tr>
                  <th 
                    className={`sortable ${sortField === 'kwh' ? sortDirection : ''}`}
                    onClick={() => handleSort('kwh')}
                  >
                    Kwh {sortField === 'kwh' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className={`sortable ${sortField === 'valor_kit' ? sortDirection : ''}`}
                    onClick={() => handleSort('valor_kit')}
                  >
                    Valor Kit {sortField === 'valor_kit' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className={`sortable ${sortField === 'porcentagem' ? sortDirection : ''}`}
                    onClick={() => handleSort('porcentagem')}
                  >
                    Porcentagem {sortField === 'porcentagem' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className={`sortable ${sortField === 'total' ? sortDirection : ''}`}
                    onClick={() => handleSort('total')}
                  >
                    Total {sortField === 'total' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Última Atualização</th>
                  {podeEditar && <th className="actions-header">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item) => (
                  <tr key={item.id}>
                    <td className="kwh-cell">
                      <span className="kwh-badge">{item.kwh}</span>
                    </td>
                    <td className="valor-cell">
                      {formatCurrency(item.valor_kit)}
                    </td>
                    <td className="porcentagem-cell">
                      <span className="porcentagem-badge">{item.porcentagem}%</span>
                    </td>
                    <td className="total-cell">
                      <strong>{formatCurrency(item.total)}</strong>
                    </td>
                    <td className="date-cell">
                      {new Date(item.data_atualizacao).toLocaleDateString('pt-BR')}
                    </td>
                    {podeEditar && (
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEdit(item)}
                            className="btn btn-sm btn-outline-primary"
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="btn btn-sm btn-outline-danger"
                            title="Excluir"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && podeEditar && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingItem ? '✏️ Editar Item' : '➕ Novo Item'}</h2>
              <button onClick={handleCloseForm} className="close-button">
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <div className="form-group">
                  <label htmlFor="kwh">Consumo Mensal (Kwh):</label>
                  <input
                    type="number"
                    id="kwh"
                    value={formData.kwh}
                    onChange={(e) => setFormData({...formData, kwh: e.target.value})}
                    className={`form-input ${formErrors.kwh ? 'error' : ''}`}
                    placeholder="Ex: 300"
                    min="1"
                    step="1"
                    required
                  />
                  {formErrors.kwh && <span className="error-message">{formErrors.kwh}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="valor_kit">Valor do Kit (R$):</label>
                  <input
                    type="number"
                    id="valor_kit"
                    value={formData.valor_kit}
                    onChange={(e) => setFormData({...formData, valor_kit: e.target.value})}
                    className={`form-input ${formErrors.valor_kit ? 'error' : ''}`}
                    placeholder="Ex: 6500.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  {formErrors.valor_kit && <span className="error-message">{formErrors.valor_kit}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="porcentagem">Porcentagem (%):</label>
                  <input
                    type="number"
                    id="porcentagem"
                    value={formData.porcentagem}
                    onChange={(e) => setFormData({...formData, porcentagem: e.target.value})}
                    className={`form-input ${formErrors.porcentagem ? 'error' : ''}`}
                    placeholder="Ex: 15.00"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                  {formErrors.porcentagem && <span className="error-message">{formErrors.porcentagem}</span>}
                </div>

                {/* Preview do Total */}
                {formData.valor_kit && formData.porcentagem && (
                  <div className="preview-total">
                    <h4>📊 Prévia do Total:</h4>
                    <div className="preview-values">
                      <div className="preview-item">
                        <span>Valor Base:</span>
                        <span>{formatCurrency(parseFloat(formData.valor_kit) || 0)}</span>
                      </div>
                      <div className="preview-item">
                        <span>Porcentagem:</span>
                        <span>{parseFloat(formData.porcentagem) || 0}%</span>
                      </div>
                      <div className="preview-item total-preview">
                        <span><strong>Total Final:</strong></span>
                        <span><strong>
                          {formatCurrency(
                            (parseFloat(formData.valor_kit) || 0) + 
                            ((parseFloat(formData.valor_kit) || 0) * (parseFloat(formData.porcentagem) || 0) / 100)
                          )}
                        </strong></span>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={handleCloseForm}
                className="btn btn-secondary"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? '⏳ Salvando...' : (editingItem ? '💾 Atualizar' : '➕ Criar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ValorKitManager
